import { connect } from 'react-redux';
import FrameworkUtils from 'utils/framework';
import { createSelector } from 'reselect';

import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import { beautyInsiderAccountSelector } from 'selectors/user/beautyInsiderAccount/beautyInsiderAccountSelector';
import purchaseHistorySelector from 'selectors/purchaseHistory/purchaseHistorySelector';
import RecentlyViewedDataSelector from 'selectors/recentlyViewedData/recentlyViewedDataSelector';
import initializedCurrentLovesSelector from 'selectors/loves/initializedCurrentLovesSelector';
import beautyRecommendationsSelector from 'selectors/beautyRecommendations/beautyRecommendationsSelector';

import recapComponentListSelector from 'viewModel/content/recapList/selectors/recapComponentListSelector';
import recapBasketComponentSelector from 'viewModel/content/recapList/selectors/recapBasketComponentSelector';
import { getRecapJSXComponentCards } from 'viewModel/content/recapList/getRecapJSXComponentCards';
import localeUtils from 'utils/LanguageLocale';

import UserActions from 'actions/UserActions';
import RvDataActions from 'actions/RvDataActions';
import HomepageActions from 'actions/HomepageActions';
import { p13nSelector } from 'selectors/p13n/p13nSelector';
import { replenP13nSelector } from 'selectors/replenP13n/replenP13nSelector';
import { rtcoP13nSelector } from 'selectors/rtcoP13n/rtcoP13nSelector';
import { puwyloBatchDataSelector } from 'selectors/p13n/puwyloBatchDataSelector';
import PuwyloHomepageUtils from 'utils/PuwyloHomepageUtils';
import { recapP13nABTestSelector } from 'selectors/recapP13nABTest/recapP13nABTestSelector';
import PersonalizationUtils from 'utils/Personalization';
import { fetchRtcoP13nData } from 'actions/RtcoP13nActions';

import { sendCmsComponentEvent } from 'analytics/utils/cmsComponents';
import constants from 'constants/content';
import anaConsts from 'analytics/constants';
import basketConsts from 'constants/Basket';
import Empty from 'constants/empty';

import itemsByBasketSelector from 'selectors/basket/itemsByBasket/itemsByBasketSelector';
import { BASKET_TYPES } from 'actions/ActionsConstants';
import store from 'store/Store';
import { getOrCreateAbTestUuid } from 'utils/localStorage/AbTestUuid';

const {
    COMPONENT_TYPES: { RECAP },
    RECAP_CAROUSEL: { ITEM_URLS }
} = constants;

const {
    CMS_COMPONENT_EVENTS: { IMPRESSION, ITEM_CLICK }
} = anaConsts;

/**
 * PUWLO recap replenishment tile: builds SOT payload fields from the P13N Content `/replen` row
 * (keyed by `item.personalization.context` in `replenishmentByContext`).
 *
 * @param {object} item - CMS recap carousel item; must use `ITEM_URLS.REPLENISHMENT` and include `personalization.context`.
 * @param {Record<string, object>} replenishmentByContext - Map of replen context id → normalized row from Redux (`replenListToByContext`).
 * @returns {{ sotV439: string, timestamp: string, personalization: object }|null} Patch merged onto the item for analytics, or `null` if not replen or no matching row.
 */
export function buildReplenSotFields(item, replenishmentByContext) {
    if (item?.action?.targetUrl !== ITEM_URLS.REPLENISHMENT) {
        return null;
    }

    const context = item?.personalization?.context;
    const replen = context ? replenishmentByContext?.[context] : null;

    if (!replen) {
        return null;
    }

    const abTestEndDate = replen.abTestEndDate ?? item.personalization?.replenAB?.abTestEndDate ?? '';
    const replenSotV439Bundle = `servedSkuNumber:${replen.servedSkuNumber || ''};replen_category:${replen.replenCategory || ''};isReplenEnabled:${!!replen.isReplenEnabled};abTestId:${replen.abTestId || ''};abTestEndDate:${abTestEndDate}`;

    return {
        // sotV439 carries the Replen-specific bundle (semicolon-separated key:value pairs).
        // The downstream SOT rule today computes sotV439 as `banner.timestamp + ';' + nbcTrackingData`
        // and ignores anything we set here, so we ALSO set `timestamp` below as a workaround.
        sotV439: replenSotV439Bundle,
        timestamp: replenSotV439Bundle,
        personalization: {
            ...(item.personalization || Empty.Object),
            isDefault: replen.isDefault,
            isControl: replen.isControl,
            isAbTest: replen.isAbTest,
            abTestEndDate: (replen.abTestEndDate ?? item.personalization?.replenAB?.abTestEndDate) || null
        }
    };
}

/**
 * PUWLO recap basket (RTCO): builds `sotV439`, mirrored `timestamp`, and AB-related `personalization`
 * from the logged-in `p13n/puwlo/rtco` row in Redux (no CMS fallbacks for promo payload).
 *
 * @param {object} item - CMS recap item whose `action.targetUrl` is the basket recap URL.
 * @param {object|undefined|null} rtcoData - Normalized RTCO API row, or `undefined`/`null` when in-flight or no row.
 * @returns {{ sotV439: string, timestamp: string, personalization: object }|null} SOT patch or `null` if not basket or unusable `rtcoData`.
 */
export function buildRtcoSotFields(item, rtcoData) {
    if (item?.action?.targetUrl !== ITEM_URLS.BASKET) {
        return null;
    }

    // In-flight or no usable API row — do not fabricate RTCO fields from CMS.
    if (rtcoData === undefined || rtcoData === null || typeof rtcoData !== 'object') {
        return null;
    }

    const personalization = item.personalization || Empty.Object;

    const abTestId = rtcoData.isAbTest === true ? rtcoData.abTestId || '' : '';
    const resolvedAbTestEndDate = rtcoData.abTestEndDate ?? rtcoData.endDate ?? '';
    // No trailing `;`: SOT / tag rules append `;` + more segments → a terminal `;` becomes `;;` in the payload.
    const rtcoSotV439Bundle = `promoType:${rtcoData.promoType || ''};biTier:${rtcoData.biTier || ''};abTestId:${abTestId};isRtcoEnabled:${!!rtcoData.isRtcoEnabled};abTestEndDate:${resolvedAbTestEndDate}`;

    return {
        sotV439: rtcoSotV439Bundle,
        timestamp: rtcoSotV439Bundle,
        personalization: {
            ...personalization,
            isDefault: rtcoData.isDefault,
            isControl: rtcoData.isControl,
            isAbTest: rtcoData.isAbTest,
            abTestEndDate: (rtcoData.abTestEndDate ?? rtcoData.endDate) || null
        }
    };
}

// PUWLO Unrecognized (logged-out) flow — items eligible for anonymous SOT
// decoration. Used to gate `buildAnonymousRecapSotFields` so it never leaks
// into recognized-user tracking.
const ANONYMOUS_RECAP_TARGET_URLS = [ITEM_URLS.BASKET, ITEM_URLS.RECENTLY_VIEWED];

/**
 * PUWLO anonymous recap: SOT fields from PUWLO `/p13n/puwlo/recap/anonymous` (`recapP13nABTestData`),
 * not from `/rtco`. Sets `sotV342`–`sotV344` and `sotV439` / `timestamp` for basket and recently-viewed tiles only.
 *
 * @param {object} item - CMS recap item (basket or recently viewed URL only).
 * @param {object|null|undefined} recapP13nABTestData - Normalized anonymous recap AB row from Redux.
 * @param {{ isAnonymous?: boolean }} user - Must be anonymous to receive decoration.
 * @returns {{ sotV439: string, timestamp: string, sotV342: boolean, sotV343: boolean, sotV344: boolean, personalization: object }|null}
 */
export function buildAnonymousRecapSotFields(item, recapP13nABTestData, user) {
    const userIsAnonymous = user?.isAnonymous;
    const isEligibleTarget = ANONYMOUS_RECAP_TARGET_URLS.includes(item?.action?.targetUrl);

    if (!userIsAnonymous || !isEligibleTarget) {
        return null;
    }

    const {
        deviceId, abTestId = null, isAbTest, isDefault, endDate = null, isRecap, isControl
    } = recapP13nABTestData || {};
    const resolvedDeviceId = deviceId || getOrCreateAbTestUuid(item.personalization?.context, endDate) || '';

    const anonymousSotV439Bundle = `deviceId:${resolvedDeviceId};isRecapEnabled:${isRecap ?? true};abTestId:${abTestId};abTestEndDate:${endDate}`;

    return {
        // sotV439 + timestamp workaround mirrors Replen / RTCO patterns
        // (downstream SOT rule reads `banner.timestamp`).
        sotV439: anonymousSotV439Bundle,
        timestamp: anonymousSotV439Bundle,
        // sotV342 / 343 / 344 set DIRECTLY on the item so they flow
        // through `mountComponentEventData`'s `{ ...item }` spread into
        // `componentData.sotV*`. The downstream Launch rule reads
        // `componentData.p13n.*` which is populated by `matchContexts` from
        // the local-storage personalization cache — anonymous PUWLO BASKET/RV
        // contexts aren't cached there, so the rule has no source to map
        // from. Matches the iOS direct-set pattern (see iOS payload screenshot).
        // The `personalization.*` fallback below is preserved for downstream
        // rules that DO read from personalization (logged-in Replen pattern).
        sotV342: !!isDefault,
        sotV343: !!isControl,
        sotV344: !!isAbTest,
        personalization: {
            ...(item.personalization || Empty.Object),
            isDefault: !!isDefault,
            isControl: !!isControl,
            isAbTest: !!isAbTest,
            abTestEndDate: endDate || null
        }
    };
}

function enrichItemWithSotFields(item, { replenishmentByContext, rtcoData, recapP13nABTestData, user }) {
    const replenSotFields = buildReplenSotFields(item, replenishmentByContext);

    if (replenSotFields) {
        return { ...item, ...replenSotFields };
    }

    // Anonymous flow takes precedence over the logged-in `/rtco` decoration:
    // anonymous BASKET / RECENTLY_VIEWED items must carry the AbTest Service
    // attributes (deviceId, isRecapEnabled, abTestId) — not the RTCO promo
    // bundle, which is a logged-in-only enhancement.
    if (user?.isAnonymous) {
        const anonymousSotFields = buildAnonymousRecapSotFields(item, recapP13nABTestData, user);

        return anonymousSotFields ? { ...item, ...anonymousSotFields } : item;
    }

    const rtcoSotFields = buildRtcoSotFields(item, rtcoData);

    return rtcoSotFields ? { ...item, ...rtcoSotFields } : item;
}

const RTCO_WAIT_TIMEOUT_MS = 4000;
const RTCO_POLL_INTERVAL_MS = 100;

const delay = waitMs => new Promise(resolve => setTimeout(resolve, waitMs));

/**
 * Reads the RTCO row from Redux, optionally polling until it settles. Only `undefined`
 * means "in-flight"; `null` means idle or settled "no row" (see p13n reducer + RtcoP13nActions).
 *
 * @param {Object} [options]
 * @param {boolean} [options.shouldWait] - When false, returns the current value without polling.
 * @param {number} [options.timeoutMs] - Max time to poll before giving up.
 * @param {number} [options.pollMs] - Interval between polls.
 * @returns {Promise<Object|null|undefined>} The RTCO row, `null` on timeout/no row, or the raw value when not waiting.
 */
export async function waitForRtcoData({ shouldWait, timeoutMs = RTCO_WAIT_TIMEOUT_MS, pollMs = RTCO_POLL_INTERVAL_MS } = {}) {
    const read = () => store.getState()?.p13n?.rtcoData;

    if (!shouldWait) {
        return read();
    }

    let latest = read();

    // Only `undefined` is in-flight; `null` is idle or settled "no row" (see p13n reducer + RtcoP13nActions).
    if (latest !== undefined) {
        return latest;
    }

    const start = Date.now();

    /* eslint-disable no-await-in-loop -- sequential poll interval until rtcoData settles or deadline */
    while (Date.now() - start <= timeoutMs) {
        latest = read();

        if (latest !== undefined) {
            return latest;
        }

        await delay(pollMs);
    }
    /* eslint-enable no-await-in-loop */

    latest = read();

    if (latest !== undefined) {
        return latest;
    }

    // Timed out while still in-flight — same as "no RTCO row" for SOT (no CMS bundle).
    return null;
}

const RECAP_AB_TEST_WAIT_TIMEOUT_MS = 4000;
const RECAP_AB_TEST_POLL_INTERVAL_MS = 100;

/**
 * Reads the PUWLO recap anonymous AB-test row from Redux, optionally polling until it settles.
 *
 * The PUWLO recap anonymous response (`/p13n/puwlo/recap/anonymous`) is fired fire-and-forget
 * from the head script (`RecapAbTest.kickOff`). IntersectionObserver often fires the carousel
 * impression before the response lands in Redux, leaving the SOT decoration empty. This helper
 * mirrors {@link waitForRtcoData}: poll the store until the row settles, time out at 4 s, then
 * return whatever is present so the caller can decorate or fall through to an undecorated impression.
 *
 * @param {Object} [options]
 * @param {boolean} [options.shouldWait] - When false, returns the current value without polling.
 * @param {number} [options.timeoutMs] - Max time to poll before giving up.
 * @param {number} [options.pollMs] - Interval between polls.
 * @returns {Promise<Object|null|undefined>} The recap AB-test row. When shouldWait is false, returns the raw store value without polling (often null/undefined before the head-script dispatch lands — i.e. still in-flight). When shouldWait is true, returns the settled row, or null once it settles as absent or the deadline passes while still in-flight — so a null result conflates "no row" and "unsettled/in-flight".
 */
export async function waitForRecapP13nABTestData({
    shouldWait,
    timeoutMs = RECAP_AB_TEST_WAIT_TIMEOUT_MS,
    pollMs = RECAP_AB_TEST_POLL_INTERVAL_MS
} = {}) {
    const read = () => store.getState()?.p13n?.recapABTestData;

    if (!shouldWait) {
        return read();
    }

    let latest = read();

    if (latest != null) {
        return latest;
    }

    const start = Date.now();

    /* eslint-disable no-await-in-loop -- sequential poll until recap AB-test row settles or deadline */
    while (Date.now() - start <= timeoutMs) {
        latest = read();

        if (latest != null) {
            return latest;
        }

        await delay(pollMs);
    }
    /* eslint-enable no-await-in-loop */

    return read() || null;
}

const { CMS_URL_PARAMS } = anaConsts;

const { POINTS_FOR_DISCOUNT_MIN_VAL } = basketConsts;

const { getPersonalizedComponent } = PersonalizationUtils;
const { wrapHOC } = FrameworkUtils;
const { recentlyViewedDataSelector } = RecentlyViewedDataSelector;
const { fetchPurchaseHistory, fetchBeautyRecommendations } = UserActions;
const { updateRvData, updateRvCategoryData } = RvDataActions;

/**
 * Whether the anonymous recap AB test is enabled for the current personalization payload.
 *
 * @param {Object} [personalization] - Personalization state slice.
 * @returns {boolean} True when the anonymous experiment is enabled.
 */
export const isRecapAnonymousExperimentEnabled = personalization =>
    personalization?.isAnonymousEnabled === true && personalization?.anonymousAB?.abTest === true;

/**
 * Derives the anonymous recap AB-test group flags (control vs challenger) from state.
 *
 * @param {Object} params
 * @param {Object} [params.personalization] - Personalization state slice.
 * @param {Object} [params.recapP13nABTestData] - Recap AB-test row from Redux.
 * @returns {{ isExperimentEnabled: boolean, isControlGroup: boolean, isChallengerGroup: boolean }}
 */
export const getRecapAnonymousGroupFlags = ({ personalization, recapP13nABTestData }) => {
    const isExperimentEnabled = isRecapAnonymousExperimentEnabled(personalization);

    return {
        isExperimentEnabled,
        isControlGroup: isExperimentEnabled && recapP13nABTestData?.isControl === true,
        isChallengerGroup:
            isExperimentEnabled &&
            recapP13nABTestData?.isAbTest === true &&
            recapP13nABTestData?.isRecap === true &&
            recapP13nABTestData?.isControl === false
    };
};

// PDE-2618 — PUWYLO Homepage Expansion carousel cards.
// Maps the normalized PUWYLO batch payload into recap carousel ITEM shapes so the
// new cards flow through the existing getRecapJSXComponentCards -> cards -> Carousel
// pipeline like every other recap card type. The backend returns cards in the
// Contentful-configured order (BR-020) and PuwyloHomepageUtils.getEligiblePuwyloCards
// preserves that order while dropping empty-product cards — the FE does NOT re-sort.
//
// Per-card presentational components (and their getRecapJSXComponentCards targetUrl
// mappings) are delivered by separate per-card stories (WBS 30-33). Until those land,
// these items have a `puwylo:`-namespaced action.targetUrl with no targetUrlMap entry,
// so getRecapJSXComponentCards falls through to its `default` (no skuList) -> null and
// renders nothing yet. Returns [] when the killswitch is off, data is null, or no card
// survives the empty-filter — leaving the existing carousel untouched (fallback).
export const buildPuwyloRecapItems = puwyloBatchData => {
    if (!PuwyloHomepageUtils.shouldUsePuwyloCarousel(puwyloBatchData)) {
        return [];
    }

    return PuwyloHomepageUtils.getEligiblePuwyloCards(puwyloBatchData.cards).map(card => ({
        sid: `puwylo-${card.contextId}`,
        contextId: card.contextId,
        cardType: card.cardType,
        title: card.title,
        clickAction: card.clickAction,
        products: card.products,
        // No targetUrlMap entry yet (deferred to per-card stories) -> default -> null.
        action: { targetUrl: `puwylo:${card.cardType}` }
    }));
};

/**
 * Applies the PUWYLO Anonymous Recap (PME-1597) filter rules for anonymous users.
 * See PRD "PUWYLO P13N - Checkout & Replen". Acceptance criteria (AC1–AC7) are annotated inline.
 * Logged-in and non-initialized users are returned unchanged (AC7).
 *
 * @param {Object} params
 * @param {Object} params.user - User state (isInitialized, isAnonymous).
 * @param {Object} params.personalization - Personalization state slice.
 * @param {Object} params.recapP13nABTestData - Recap AB-test row from Redux.
 * @param {Array} params.items - Recap carousel items to filter.
 * @param {number} params.basketTotalItemCount - Total items in the basket.
 * @param {Object} params.rvData - Recently viewed data.
 * @returns {Array} The filtered items (possibly empty).
 */
export const filterAnonymousRecapItems = ({
    user, personalization, recapP13nABTestData, items, basketTotalItemCount, rvData
}) => {
    const safeItems = Array.isArray(items) ? items : [];
    const { isExperimentEnabled, isControlGroup, isChallengerGroup } = getRecapAnonymousGroupFlags({
        personalization,
        recapP13nABTestData
    });

    // AC7: No Impact to Logged-In Flow — return items unchanged for recognized users
    // and any non-initialized user state. All anon-specific gating below is bypassed.
    if (!user?.isInitialized || !user?.isAnonymous) {
        return safeItems;
    }

    // AC1: Hide Recap entirely for anonymous users when isAnonymousEnabled is not
    // explicitly true at the recap level (CMS toggle off → feature unavailable).
    if (personalization?.isAnonymousEnabled !== true) {
        return [];
    }

    // AC1: A/B Test Based Visibility. AB-test gating only applies when the
    // experiment is enabled. When isAnonymousEnabled is true but anonymousAB.abTest
    // is false the feature is rolled out to all anonymous users — no backend call,
    // no challenger gating.
    if (isExperimentEnabled) {
        // AC1: Control group → do NOT show carousel or any cards.
        if (isControlGroup) {
            return [];
        }

        // AC1: Challenger-group-only requirement — if the AB test data is missing
        // (request failed / pending), do not render the module to avoid a flicker
        // between Control and Challenger experiences.
        if (!recapP13nABTestData) {
            return [];
        }

        // AC1: Non-test / non-challenger traffic → hide the module.
        if (!isChallengerGroup) {
            return [];
        }
    }

    const hasBasketItems = (basketTotalItemCount ?? 0) > 0;
    const hasRecentlyViewedItems = Array.isArray(rvData) && rvData.length > 0;

    // AC8: Fallback Behavior — no basket items AND no Recently Viewed items →
    // PUWYLO carousel should NOT be displayed.
    if (!hasBasketItems && !hasRecentlyViewedItems) {
        return [];
    }

    // AC3 / AC4 / AC5 / AC6: Card Rendering — anonymous Challenger (and rolled-out)
    // users only see basket (RTCO) and Recently Viewed cards. Other recap targets
    // (purchase history, loves, beauty recs, replen, bestsellers, etc.) are excluded.
    // - hasBasket only          → RTCO card (AC3, AC6)
    // - hasRV only              → Recently Viewed card (AC4, AC6)
    // - hasBasket AND hasRV     → both cards (AC5)
    const allowedTargets = [...(hasBasketItems ? [ITEM_URLS.BASKET] : []), ...(hasRecentlyViewedItems ? [ITEM_URLS.RECENTLY_VIEWED] : [])];

    // Per-item gate: each surviving CMS item must also opt in via its own
    // personalization.isAnonymousEnabled flag — protects against accidental rollout
    // of cards that aren't ready for anonymous traffic.
    return safeItems.filter(item => allowedTargets.includes(item?.action?.targetUrl)).filter(item => item.personalization?.isAnonymousEnabled);
};

function replenListToByContext(replenP13nData) {
    const list = Array.isArray(replenP13nData) ? replenP13nData : replenP13nData ? [replenP13nData] : [];

    return list.reduce((acc, item) => {
        if (item?.context) {
            acc[item.context] = item;
        }

        return acc;
    }, {});
}

function buildRecapData({
    requiredData, purchaseHistory, rvData, currentLovesData, basket, beautyRecommendations, replenP13nData, rtcoP13nData
}) {
    const currentPurchaseHistory = purchaseHistory?.items || [];
    const currentRvData = rvData || [];
    const currentBeautyRecommendations = beautyRecommendations?.items || [];

    return {
        ...(requiredData.purchaseHistory && {
            purchaseHistory: {
                items: currentPurchaseHistory,
                dataReady: Array.isArray(currentPurchaseHistory)
            }
        }),
        ...(requiredData.rvData && {
            rvData: {
                items: currentRvData,
                dataReady: Array.isArray(currentRvData)
            }
        }),
        ...(requiredData.currentLovesData && {
            currentLovesData: {
                items: currentLovesData.currentLoves,
                dataReady: currentLovesData.currentLovesIsInitialized
            }
        }),
        ...(requiredData.basket && {
            basket: {
                ...basket,
                dataReady: basket.isInitialized || basket.totalItemCount === 0
            }
        }),
        ...(requiredData.beautyRecommendations && {
            beautyRecommendations: {
                items: currentBeautyRecommendations,
                dataReady: Array.isArray(currentBeautyRecommendations)
            }
        }),
        ...(requiredData.replenishment && {
            replenishmentByContext: replenListToByContext(replenP13nData),
            replenishment: replenP13nData ?? null,
            replenDataReady: replenP13nData !== undefined
        }),
        // `null` until fetch starts; `undefined` in-flight; object = row (RtcoP13nActions / p13n reducer).
        rtcoData: rtcoP13nData,
        rtcoDataReady: rtcoP13nData !== undefined
    };
}

export default wrapHOC(
    connect(
        createSelector(
            coreUserDataSelector,
            recapComponentListSelector,
            purchaseHistorySelector,
            recentlyViewedDataSelector,
            initializedCurrentLovesSelector,
            recapBasketComponentSelector,
            beautyRecommendationsSelector,
            p13nSelector,
            beautyInsiderAccountSelector,
            itemsByBasketSelector,
            replenP13nSelector,
            rtcoP13nSelector,
            recapP13nABTestSelector,
            puwyloBatchDataSelector,
            (_state, ownProps) => ownProps.title,
            (_state, ownProps) => ownProps.personalization,
            (_state, ownProps) => ownProps,
            // eslint-disable-next-line complexity
            (
                user,
                { componentList, ...requiredData },
                purchaseHistory,
                { rvData },
                currentLovesData,
                basket,
                beautyRecommendations,
                p13n,
                beautyInsiderAccount,
                itemsByBasket = [],
                replenP13nData,
                rtcoP13nData,
                recapP13nABTestData,
                puwyloBatchData,
                carouselTitle,
                personalization,
                ownProps
            ) => {
                const data = buildRecapData({
                    requiredData,
                    purchaseHistory,
                    rvData,
                    currentLovesData,
                    basket,
                    beautyRecommendations,
                    replenP13nData,
                    rtcoP13nData
                });

                const personalizedComponent = getPersonalizedComponent(personalization, user, p13n, false);
                const personalizedItems = personalizedComponent?.variationData?.items;
                const { isControlGroup: isAnonymousControlGroup } = getRecapAnonymousGroupFlags({
                    personalization,
                    recapP13nABTestData
                });

                let items = Array.isArray(personalizedItems) ? personalizedItems : componentList;
                const userId = user.userId;
                const isLoading = !Array.isArray(items);
                // Snapshot items BEFORE the anonymous filter so control-group
                // anonymous tracking (carousel hidden) can still reach the
                // original BASKET / RV items for measurement parity.
                const preFilterItems = items;

                items = filterAnonymousRecapItems({
                    user,
                    personalization,
                    recapP13nABTestData,
                    items,
                    basketTotalItemCount: basket?.totalItemCount,
                    rvData
                });

                // PDE-2618: append PUWYLO Homepage Expansion cards to the recap item list
                // so they flow through the existing cards pipeline. No-op (returns []) when
                // the killswitch is off, batch data is null, or no card survives the filter.
                const puwyloRecapItems = buildPuwyloRecapItems(puwyloBatchData);

                if (Array.isArray(items) && puwyloRecapItems.length > 0) {
                    items = [...items, ...puwyloRecapItems];
                }

                const hasRtcoCard = Array.isArray(items) && items.some(i => i?.action?.targetUrl === ITEM_URLS.BASKET);

                // Click / impression analytics: triggerClick, triggerRecapImpression below.
                // RecapItem / EmptyRecapItem await triggerClick before Location.navigateTo so
                // sendCmsComponentEvent can finish before non-SPA navigation — except logged-in
                // basket clicks: RTCO poll can take seconds, so that path runs detached.
                const triggerClick = async function (targetSid, position) {
                    const { sid, title } = ownProps;
                    const eventName = ITEM_CLICK;
                    const clickedItem = items?.find(i => i?.sid === targetSid);
                    const replenishmentByContext = data.replenishmentByContext || Empty.Object;
                    const shouldWaitForRtco = !user.isAnonymous && clickedItem?.action?.targetUrl === ITEM_URLS.BASKET;
                    // PUWLO Unrecognized — wait for the AbTest Service row when
                    // an anonymous user clicks an eligible card so SOT fields
                    // (sotV439 / 342–344) are populated even if the response
                    // hasn't landed yet at click time.
                    const shouldWaitForAnonAbTest =
                        user.isAnonymous &&
                        ANONYMOUS_RECAP_TARGET_URLS.includes(clickedItem?.action?.targetUrl) &&
                        isRecapAnonymousExperimentEnabled(personalization);

                    const sendClickWithSotData = async ({ rtcoData, freshRecapAbTestData }) => {
                        const _items = items
                            .filter(item => item.sid === targetSid)
                            .map(item => ({
                                ...item,
                                itemIndex: position
                            }))
                            .map(item =>
                                enrichItemWithSotFields(item, {
                                    replenishmentByContext,
                                    rtcoData,
                                    // Prefer the fresh value resolved at click time over the
                                    // closure value, which may be stale if AbTest Service responded
                                    // after this connect mapping ran.
                                    recapP13nABTestData: freshRecapAbTestData ?? recapP13nABTestData,
                                    user
                                })
                            );

                        await sendCmsComponentEvent({
                            items: _items,
                            eventName,
                            title,
                            sid,
                            clickedSid: targetSid,
                            component: RECAP
                        });
                    };

                    if (shouldWaitForRtco) {
                        (async () => {
                            const rtcoData = await waitForRtcoData({ shouldWait: true });

                            await sendClickWithSotData({ rtcoData });
                        })().catch(error => {
                            Sephora?.logger?.error('triggerRecapClick failed', error);
                        });

                        return;
                    }

                    if (shouldWaitForAnonAbTest) {
                        (async () => {
                            const [rtcoData, freshRecapAbTestData] = await Promise.all([
                                waitForRtcoData({ shouldWait: false }),
                                waitForRecapP13nABTestData({ shouldWait: true })
                            ]);

                            await sendClickWithSotData({ rtcoData, freshRecapAbTestData });
                        })().catch(error => {
                            Sephora?.logger?.error('triggerRecapClick (anonymous) failed', error);
                        });

                        return;
                    }

                    const rtcoData = await waitForRtcoData({ shouldWait: false });

                    await sendClickWithSotData({ rtcoData });
                };

                let basketItemsCounts = {};
                let isBopisOnly = false;
                let hasStandardItems = false;

                // ========================================================================
                // AC7: No Impact to Logged-In Flow
                // Beauty Insider points are only available for logged-in users
                // Guard ensures logged-in users continue to see their benefits
                // ========================================================================
                const hasSufficientPoints = !user.isAnonymous && beautyInsiderAccount?.promotionPoints >= POINTS_FOR_DISCOUNT_MIN_VAL;
                const showApplyPointsInReadyToCheckoutSection =
                    (localeUtils.isUS() && Sephora.isMobile()) || (localeUtils.isCanada() && !Sephora.isMobile());

                // AC7: Allow basket metrics for both logged-in AND anonymous users
                // (anonymous users can see "Free Shipping" messages in RTCO card)
                // AC7: Logged-in flow unchanged - still receives all basket data
                if (user.isInitialized) {
                    basketItemsCounts = Object.keys(BASKET_TYPES).reduce((counts, basketTypeKey) => {
                        const basketType = BASKET_TYPES[basketTypeKey];
                        counts[basketTypeKey] = itemsByBasket.find(item => item.basketType === basketType)?.itemsCount || 0;

                        return counts;
                    }, {});

                    isBopisOnly =
                        basketItemsCounts.BOPIS_BASKET > 0 &&
                        Object.keys(basketItemsCounts).every(key => key === 'BOPIS_BASKET' || basketItemsCounts[key] === 0);

                    hasStandardItems = basketItemsCounts.STANDARD_BASKET > 0;
                }

                // a card in cards array will be rendered in carousel
                // a rendered card might be in a skeleton state or content state
                const cards = getRecapJSXComponentCards({
                    isLoading,
                    items,
                    urlParams: {
                        [CMS_URL_PARAMS.cRefid]: ownProps?.sid
                    },
                    data,
                    carouselTitle,
                    triggerClick,
                    showApplyPointsInReadyToCheckoutSection,
                    hasStandardItems,
                    isBopisOnly,
                    hasSufficientPoints
                });

                const mountItems = cards.map(card => ({
                    ...card.props,
                    uniqueSkus: card.props?.uniqueSkus || [],
                    skus: card.props?.skus || {}
                }));

                // AC2: Trigger impression / viewable / click events for users who
                // see cards. Note: Control-group anonymous users hit hideRow=true
                // below and Recap.ctrlr renders null, so this callback never fires
                // for them. AB-test bucketing for Control is captured server-side via
                // the getP13nRecapABTestData call. If product analytics needs UI
                // impressions for both groups, fire imperatively in Recap.ctrlr.
                const triggerRecapImpression = targets => {
                    if (isLoading) {
                        return;
                    }

                    (async () => {
                        const { title, sid } = ownProps;
                        const replenishmentByContext = data.replenishmentByContext || Empty.Object;
                        const shouldWaitForRtcoData =
                            !user.isAnonymous && hasRtcoCard && targets.some(index => mountItems[index]?.action?.targetUrl === ITEM_URLS.BASKET);
                        // PUWLO Unrecognized — IntersectionObserver fires the
                        // impression as soon as the carousel is visible, often
                        // before AbTest Service has responded. Wait so SOT
                        // fields (sotV439 / 342–344) reach the beacon.
                        const shouldWaitForAnonAbTest =
                            user.isAnonymous &&
                            targets.some(index => ANONYMOUS_RECAP_TARGET_URLS.includes(mountItems[index]?.action?.targetUrl)) &&
                            isRecapAnonymousExperimentEnabled(personalization);

                        const [rtcoData, freshRecapAbTestData] = await Promise.all([
                            waitForRtcoData({ shouldWait: shouldWaitForRtcoData }),
                            waitForRecapP13nABTestData({ shouldWait: shouldWaitForAnonAbTest })
                        ]);

                        const currentItems = mountItems
                            .map((item, index) => ({
                                ...item,
                                itemIndex: index
                            }))
                            .filter((_item, index) => targets.includes(index))
                            .map(item =>
                                enrichItemWithSotFields(item, {
                                    replenishmentByContext,
                                    rtcoData,
                                    recapP13nABTestData: freshRecapAbTestData ?? recapP13nABTestData,
                                    user
                                })
                            );

                        const eventName = IMPRESSION;
                        setTimeout(() => {
                            sendCmsComponentEvent({
                                items: currentItems,
                                eventName,
                                title,
                                sid,
                                component: RECAP
                            });
                        }, 1000);
                    })().catch(error => {
                        Sephora?.logger?.error('triggerRecapImpression failed', error);
                    });
                };

                // Replen items where the user is bucketed into the A/B test control group.
                // The card itself is hidden (see getRecapJSXComponentCards.js), so the Carousel
                // never reports an impression — we fire one explicitly on PUWLO mount so the
                // control bucket can still be measured against the challenger.
                const controlReplenItems =
                    data.replenDataReady && Array.isArray(items)
                        ? items.filter(item => {
                            if (item?.action?.targetUrl !== ITEM_URLS.REPLENISHMENT) {
                                return false;
                            }

                            const ctx = item?.personalization?.context;
                            const replen = ctx ? data.replenishmentByContext?.[ctx] : null;

                            return Boolean(replen?.isControl && replen?.isAbTest);
                        })
                        : [];

                const triggerControlReplenImpression = () => {
                    if (!controlReplenItems.length) {
                        return;
                    }

                    const { title, sid } = ownProps;
                    const replenishmentByContext = data.replenishmentByContext || Empty.Object;
                    const decoratedItems = controlReplenItems.map((item, index) => {
                        const baseItem = { ...item, itemIndex: index };

                        return enrichItemWithSotFields(baseItem, {
                            replenishmentByContext,
                            rtcoData: store.getState()?.p13n?.rtcoData,
                            recapP13nABTestData,
                            user
                        });
                    });

                    sendCmsComponentEvent({
                        items: decoratedItems,
                        eventName: IMPRESSION,
                        title,
                        sid,
                        component: RECAP
                    });
                };

                // PUWLO Unrecognized — control-group anonymous users have the
                // carousel hidden (filterAnonymousRecapItems → []). To preserve
                // measurement parity with the challenger group, fire ONE
                // impression carrying the anonymous AB-test SOT fields. Mirrors
                // `triggerControlReplenImpression` for the logged-in flow.
                const controlAnonymousItems =
                    user?.isAnonymous && isAnonymousControlGroup && Array.isArray(preFilterItems)
                        ? preFilterItems.filter(item => ANONYMOUS_RECAP_TARGET_URLS.includes(item?.action?.targetUrl))
                        : [];

                const hasControlAnonymousImpression = controlAnonymousItems.length > 0 && Boolean(recapP13nABTestData);

                const triggerControlAnonymousImpression = () => {
                    if (!hasControlAnonymousImpression) {
                        return;
                    }

                    const { title, sid } = ownProps;
                    const replenishmentByContext = data.replenishmentByContext || Empty.Object;
                    const decoratedItems = controlAnonymousItems.map((item, index) => {
                        const baseItem = { ...item, itemIndex: index };

                        return enrichItemWithSotFields(baseItem, {
                            replenishmentByContext,
                            rtcoData: store.getState()?.p13n?.rtcoData,
                            recapP13nABTestData,
                            user
                        });
                    });

                    sendCmsComponentEvent({
                        items: decoratedItems,
                        eventName: IMPRESSION,
                        title,
                        sid,
                        component: RECAP
                    });
                };

                // entire carousel is hidden initially to avoid CLS issues for anonymous users (EXP-4648)
                // AC1 / AC8: Hide row entirely when there are no cards. For anonymous
                // users this catches:
                //   - Control group       (filterAnonymousRecapItems returned [])
                //   - isAnonymousEnabled false (filter returned [])
                //   - No basket AND no RV data (filter returned [])
                // For logged-in users this only triggers if the recap component list
                // is empty in the CMS payload.
                let hideRow = true;

                if (user.isInitialized && cards.length > 0) {
                    hideRow = false;
                }

                // AC3 / AC4 / AC5 / AC6: anonymousSubtitle Banner from CMS — rich text
                // with embedded "Sign In" link rendered above the carousel for anonymous
                // Challenger users only, and only when the carousel has cards to render.
                // Control / hidden / non-challenger states produce no CTA.
                let anonymousSubtitle = null;

                if (user.isAnonymous && personalization?.isAnonymousEnabled && cards.length > 0 && ownProps.anonymousSubtitle) {
                    anonymousSubtitle = ownProps.anonymousSubtitle;
                }

                // Return props to Recap component
                return {
                    userId,
                    hideRow,
                    isLoading,
                    cards,
                    requiredData,
                    triggerRecapImpression,
                    hasControlReplenImpression: controlReplenItems.length > 0,
                    triggerControlReplenImpression,
                    hasControlAnonymousImpression,
                    triggerControlAnonymousImpression,
                    componentList: items,
                    items,
                    isAnonymous: user.isAnonymous,
                    anonymousSubtitle
                };
            }
        ),
        {
            fetchPurchaseHistory,
            updateRvData,
            updateRvCategoryData,
            fetchBeautyRecommendations,
            fetchRtcoP13nData,
            fetchPuwyloBatchData: HomepageActions.fetchPuwyloBatchData,
            clearRecapAbTestData: () => HomepageActions.setRecapP13nAbTestData(null)
        }
    )
);
